import React from 'react';
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator';

import { UxContext } from '@client/context/UxContext';
import useDateTimeFormatters from '@client/hooks/useDateTimeFormatters';
import { useGlobalStore } from '@client/store/globalStore/context/globalContext';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import { type DateFormat, type WifiApSettings } from '@ledium/types';
import { WIFI_AP_PASSPHRASE_REGEX, WIFI_AP_SSID_REGEX } from '@ledium/validation-schemas';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Divider, FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

ValidatorForm.addValidationRule('validSsid', (value: string) => WIFI_AP_SSID_REGEX.test(value));
ValidatorForm.addValidationRule('validPassphrase', (value: string) => WIFI_AP_PASSPHRASE_REGEX.test(value));

const defaultWifiApData: WifiApSettings = { ssid: '', passphrase: '' };

function getDateFieldOrder(dateFormat: DateFormat | null): Array<'year' | 'month' | 'day'> {
    if (dateFormat === 'YYYY-MM-DD') {
        return ['year', 'month', 'day'];
    }
    if (dateFormat === 'MM/DD/YYYY') {
        return ['month', 'day', 'year'];
    }
    return ['day', 'month', 'year'];
}

interface DtFields {
    year: number;
    month: number;
    day: number;
    hour24: number;
    hour12: number;
    amPm: 'AM' | 'PM';
    minute: number;
    second: number;
}

function computeIsDtDirty(dt: DtFields, initialDt: Date, timeFormat: string | null): boolean {
    if (dt.year !== initialDt.getFullYear()) {
        return true;
    }
    if (dt.month !== initialDt.getMonth() + 1) {
        return true;
    }
    if (dt.day !== initialDt.getDate()) {
        return true;
    }
    if (timeFormat === '24') {
        if (dt.hour24 !== initialDt.getHours()) {
            return true;
        }
    } else if (dt.hour12 !== (initialDt.getHours() % 12 || 12) || dt.amPm !== (initialDt.getHours() < 12 ? 'AM' : 'PM')) {
        return true;
    }
    if (dt.minute !== initialDt.getMinutes()) {
        return true;
    }
    if (dt.second !== initialDt.getSeconds()) {
        return true;
    }
    return false;
}

interface SystemTabProps {
    helperUnavailable: boolean;
    isOnline: boolean;
    offlineMode: boolean;
    initialDeviceName: string;
    onOfflineModeChange: (mode: boolean) => void;
    onBlock: (text: string | null) => void;
}

function SystemTab({ helperUnavailable, isOnline, offlineMode, initialDeviceName, onOfflineModeChange, onBlock }: SystemTabProps) {
    const {
        globalState: { serverTimeOffset, dateFormat: savedDateFormat, timeFormat: savedTimeFormat },
        globalActions
    } = useGlobalStore();
    const { handleSnackbar, handleAlert } = React.useContext(UxContext)!;
    const { formatDate, formatTime } = useDateTimeFormatters();

    const [deviceName, setDeviceName] = React.useState('');
    const [wifiApData, setWifiApData] = React.useState<WifiApSettings>(defaultWifiApData);
    const [showPassphrase, setShowPassphrase] = React.useState(false);
    const initialDt = React.useRef(new Date(Date.now() + serverTimeOffset));
    const [dtYear, setDtYear] = React.useState(initialDt.current.getFullYear());
    const [dtMonth, setDtMonth] = React.useState(initialDt.current.getMonth() + 1);
    const [dtDay, setDtDay] = React.useState(initialDt.current.getDate());
    const [dtHour24, setDtHour24] = React.useState(initialDt.current.getHours());
    const [dtHour12, setDtHour12] = React.useState(initialDt.current.getHours() % 12 || 12);
    const [dtAmPm, setDtAmPm] = React.useState<'AM' | 'PM'>(initialDt.current.getHours() < 12 ? 'AM' : 'PM');
    const [dtMinute, setDtMinute] = React.useState(initialDt.current.getMinutes());
    const [dtSecond, setDtSecond] = React.useState(initialDt.current.getSeconds());

    const isDtDirty = computeIsDtDirty(
        { year: dtYear, month: dtMonth, day: dtDay, hour24: dtHour24, hour12: dtHour12, amPm: dtAmPm, minute: dtMinute, second: dtSecond },
        initialDt.current,
        savedTimeFormat
    );
    const wifiApValid = WIFI_AP_SSID_REGEX.test(wifiApData.ssid) && WIFI_AP_PASSPHRASE_REGEX.test(wifiApData.passphrase);
    const disabledSection = helperUnavailable ? { opacity: 0.5, pointerEvents: 'none' as const } : undefined;
    const SectionWarningIcon = () => (helperUnavailable ? <WarningAmberIcon sx={{ color: 'warning.main', fontSize: '1.2em' }} /> : null);

    React.useEffect(() => {
        setDeviceName(initialDeviceName);
    }, [initialDeviceName]);

    React.useEffect(() => {
        lediumApi.get<WifiApSettings>('settings/getWifiAp').then((data) => {
            if (data) {
                setWifiApData(data);
            }
        });
    }, []);

    async function onOfflineModeToggle(event: React.ChangeEvent<HTMLInputElement>) {
        onOfflineModeChange(event.target.checked);
        const result = await lediumApi.patch('settings/offlineMode', { enabled: event.target.checked });
        if (result) {
            handleSnackbar({ message: i18n('settings_saved') as string });
        } else {
            handleSnackbar({ message: i18n('settings_save_error') as string, severity: 'error' });
        }
    }

    function onDeviceNameSave() {
        lediumApi.patch<{ success?: boolean }>('settings/deviceName', { deviceName }).then((data) => {
            if (data?.success === true) {
                handleSnackbar({ message: i18n('device_name_saved') as string });
            }
        });
    }

    async function onSetDateTime() {
        let hour = dtHour24;
        if (savedTimeFormat === '12') {
            hour = dtAmPm === 'AM' ? dtHour12 % 12 : (dtHour12 % 12) + 12;
        }
        const date = new Date(dtYear, dtMonth - 1, dtDay, hour, dtMinute, dtSecond);
        const timestamp = Math.floor(date.getTime() / 1000);
        const data = await lediumApi.post<{ success?: boolean; serverTime?: number }>('settings/setDateTime', { timestamp });
        if (data?.success === true && data.serverTime !== undefined) {
            const pad = (n: number) => String(n).padStart(2, '0');
            const serverNow = new Date(data.serverTime);
            const timeStr = `${pad(serverNow.getHours())}:${pad(serverNow.getMinutes())}:${pad(serverNow.getSeconds())}`;
            handleSnackbar({ message: i18n('date_time_set', formatDate(data.serverTime), formatTime(timeStr)) as string });
            globalActions.api.setServerTimeOffset(data.serverTime);
            initialDt.current = serverNow;
        } else {
            handleSnackbar({ message: i18n('settings_save_error') as string, severity: 'error' });
        }
    }

    function handleWifiApChange(event: React.ChangeEvent<HTMLInputElement>) {
        const name = event.target.name;
        setWifiApData({ ...wifiApData, ...{ [name]: event.target.value } });
    }

    function onWifiApSubmit() {
        lediumApi.post<{ success?: boolean }>('settings/setWifiAp', wifiApData).then((data) => {
            if (data?.success === true) {
                handleSnackbar({ message: i18n('wifi_ap_updated') as string });
            }
        });
    }

    function onReboot() {
        handleAlert({
            title: i18n('reboot') as string,
            message: i18n('reboot_confirm') as string,
            okCallback: async () => {
                const data = await lediumApi.post<{ success: boolean }>('settings/reboot', {});
                if (data?.success) {
                    onBlock(i18n('rebooting') as string);
                } else {
                    handleSnackbar({ message: i18n('action_failed') as string, severity: 'error' });
                }
            }
        });
    }

    function onShutdown() {
        handleAlert({
            title: i18n('shutdown') as string,
            message: i18n('shutdown_confirm') as string,
            okCallback: async () => {
                const data = await lediumApi.post<{ success: boolean }>('settings/halt', {});
                if (data?.success) {
                    onBlock(i18n('shutting_down') as string);
                } else {
                    handleSnackbar({ message: i18n('action_failed') as string, severity: 'error' });
                }
            }
        });
    }

    function onRestartServerApp() {
        handleAlert({
            title: i18n('restart_server_app') as string,
            message: i18n('restart_server_app_confirm') as string,
            okCallback: async () => {
                const data = await lediumApi.post<{ success: boolean }>('settings/restartServerApp', {});
                if (data?.success) {
                    onBlock(i18n('restarting_server_app') as string);
                } else {
                    handleSnackbar({ message: i18n('action_failed') as string, severity: 'error' });
                }
            },
            cancelText: i18n('cancel') as string
        });
    }

    return (
        <>
            <Grid container spacing={3} sx={{ marginTop: '20px' }}>
                <Grid item xs={12}>
                    <Typography variant="h2">{i18n('offline_mode')}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ marginTop: '4px' }}>
                        {i18n('offline_mode_description')}
                    </Typography>
                    {isOnline && (
                        <Typography variant="body2" sx={{ marginTop: '4px', color: 'error.dark' }}>
                            {i18n('offline_mode_online_warning')}
                        </Typography>
                    )}
                </Grid>

                <Grid item xs={12} sx={{ textAlign: 'center' }}>
                    <FormControlLabel
                        control={<Switch checked={offlineMode} onChange={onOfflineModeToggle} color="primary" />}
                        label={i18n('offline_mode_enabled') as string}
                    />
                </Grid>
            </Grid>

            <Divider sx={{ marginTop: '32px' }} />

            {offlineMode && !isOnline && (
                <>
                    <Grid container spacing={3} sx={{ marginTop: '20px' }}>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SectionWarningIcon />
                                <Typography variant="h2" color={helperUnavailable ? 'text.secondary' : undefined}>
                                    {i18n('set_date_and_time')}
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ marginTop: '4px' }}>
                                {i18n('set_date_and_time_description')}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sx={disabledSection}>
                            <Grid container spacing={3} sx={{ maxWidth: '50%', mx: 'auto' }}>
                                {getDateFieldOrder(savedDateFormat).map((field) => {
                                    if (field === 'year') {
                                        return (
                                            <Grid key="year" item xs={4}>
                                                <TextField
                                                    fullWidth
                                                    label={i18n('year') as string}
                                                    type="number"
                                                    value={dtYear}
                                                    onChange={(e) => setDtYear(Number(e.target.value))}
                                                    inputProps={{ min: 2000, max: 2099 }}
                                                />
                                            </Grid>
                                        );
                                    }
                                    if (field === 'month') {
                                        return (
                                            <Grid key="month" item xs={4}>
                                                <FormControl fullWidth>
                                                    <InputLabel>{i18n('month')}</InputLabel>
                                                    <Select value={dtMonth} label={i18n('month')} onChange={(e) => setDtMonth(Number(e.target.value))}>
                                                        {Array.from({ length: 12 }, (_, i) => (
                                                            <MenuItem key={i + 1} value={i + 1}>
                                                                {String(i + 1).padStart(2, '0')}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        );
                                    }
                                    return (
                                        <Grid key="day" item xs={4}>
                                            <FormControl fullWidth>
                                                <InputLabel>{i18n('day')}</InputLabel>
                                                <Select value={dtDay} label={i18n('day')} onChange={(e) => setDtDay(Number(e.target.value))}>
                                                    {Array.from({ length: 31 }, (_, i) => (
                                                        <MenuItem key={i + 1} value={i + 1}>
                                                            {String(i + 1).padStart(2, '0')}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Grid>

                        <Grid item xs={12} sx={disabledSection}>
                            <Grid container spacing={3} sx={{ maxWidth: savedTimeFormat === '12' ? '70%' : '50%', mx: 'auto' }}>
                                {savedTimeFormat === '24' ? (
                                    <>
                                        <Grid item xs={4}>
                                            <FormControl fullWidth>
                                                <InputLabel>{i18n('hour')}</InputLabel>
                                                <Select value={dtHour24} label={i18n('hour')} onChange={(e) => setDtHour24(Number(e.target.value))}>
                                                    {Array.from({ length: 24 }, (_, i) => (
                                                        <MenuItem key={i} value={i}>
                                                            {String(i).padStart(2, '0')}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <FormControl fullWidth>
                                                <InputLabel>{i18n('minute')}</InputLabel>
                                                <Select value={dtMinute} label={i18n('minute')} onChange={(e) => setDtMinute(Number(e.target.value))}>
                                                    {Array.from({ length: 60 }, (_, i) => (
                                                        <MenuItem key={i} value={i}>
                                                            {String(i).padStart(2, '0')}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <FormControl fullWidth>
                                                <InputLabel>{i18n('second')}</InputLabel>
                                                <Select value={dtSecond} label={i18n('second')} onChange={(e) => setDtSecond(Number(e.target.value))}>
                                                    {Array.from({ length: 60 }, (_, i) => (
                                                        <MenuItem key={i} value={i}>
                                                            {String(i).padStart(2, '0')}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </>
                                ) : (
                                    <>
                                        <Grid item xs={3}>
                                            <FormControl fullWidth>
                                                <InputLabel>{i18n('hour')}</InputLabel>
                                                <Select value={dtHour12} label={i18n('hour')} onChange={(e) => setDtHour12(Number(e.target.value))}>
                                                    {Array.from({ length: 12 }, (_, i) => (
                                                        <MenuItem key={i + 1} value={i + 1}>
                                                            {String(i + 1).padStart(2, '0')}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <FormControl fullWidth>
                                                <InputLabel>{i18n('am_pm')}</InputLabel>
                                                <Select value={dtAmPm} label={i18n('am_pm')} onChange={(e) => setDtAmPm(e.target.value as 'AM' | 'PM')}>
                                                    <MenuItem value="AM">AM</MenuItem>
                                                    <MenuItem value="PM">PM</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <FormControl fullWidth>
                                                <InputLabel>{i18n('minute')}</InputLabel>
                                                <Select value={dtMinute} label={i18n('minute')} onChange={(e) => setDtMinute(Number(e.target.value))}>
                                                    {Array.from({ length: 60 }, (_, i) => (
                                                        <MenuItem key={i} value={i}>
                                                            {String(i).padStart(2, '0')}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <FormControl fullWidth>
                                                <InputLabel>{i18n('second')}</InputLabel>
                                                <Select value={dtSecond} label={i18n('second')} onChange={(e) => setDtSecond(Number(e.target.value))}>
                                                    {Array.from({ length: 60 }, (_, i) => (
                                                        <MenuItem key={i} value={i}>
                                                            {String(i).padStart(2, '0')}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Grid>

                        <Grid item xs={12} sx={{ textAlign: 'center', ...disabledSection }}>
                            <Button variant="contained" color="primary" disabled={!isDtDirty} onClick={onSetDateTime}>
                                {i18n('set_date_and_time_cta')}
                            </Button>
                        </Grid>
                    </Grid>

                    <Divider sx={{ marginTop: '32px' }} />
                </>
            )}

            <Grid container spacing={3} sx={{ marginTop: '20px' }}>
                <Grid item xs={12}>
                    <Typography variant="h2">{i18n('device_name')}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ marginTop: '4px' }}>
                        {i18n('device_name_description')}
                    </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        label={i18n('device_name_label')}
                        variant="outlined"
                        value={deviceName}
                        onChange={(event) => setDeviceName(event.target.value)}
                        inputProps={{ maxLength: 64 }}
                    />
                </Grid>

                <Grid item xs={12} sx={{ textAlign: 'center' }}>
                    <Button disabled={!deviceName.trim()} variant="contained" color="primary" onClick={onDeviceNameSave}>
                        {i18n('device_name_rename')}
                    </Button>
                </Grid>
            </Grid>

            <Divider sx={{ marginTop: '32px' }} />

            <Grid container spacing={3} sx={{ marginTop: '20px' }}>
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SectionWarningIcon />
                        <Typography variant="h2" color={helperUnavailable ? 'text.secondary' : undefined}>
                            {i18n('restart_server_app')}
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ marginTop: '4px' }}>
                        {i18n('restart_server_app_description')}
                    </Typography>
                </Grid>

                <Grid item xs={12} sx={{ ...disabledSection, textAlign: 'center' }}>
                    <Button variant="contained" color="warning" disabled={helperUnavailable} onClick={onRestartServerApp}>
                        {i18n('restart_server_app_button')}
                    </Button>
                </Grid>
            </Grid>

            <Divider sx={{ marginTop: '32px' }} />

            <ValidatorForm onSubmit={onWifiApSubmit}>
                <Grid container spacing={3} sx={{ marginTop: '20px' }}>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SectionWarningIcon />
                            <Typography variant="h2" color={helperUnavailable ? 'text.secondary' : undefined}>
                                {i18n('wifi_ap_settings')}
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ marginTop: '4px' }}>
                            {i18n('wifi_ap_settings_description')}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sx={disabledSection}>
                        <Grid container spacing={3} sx={{ maxWidth: '50%', mx: 'auto' }}>
                            <Grid item xs={12} md={6}>
                                <TextValidator
                                    fullWidth
                                    label={i18n('wifi_ap_ssid')}
                                    variant="outlined"
                                    name="ssid"
                                    onChange={handleWifiApChange}
                                    value={wifiApData.ssid}
                                    validators={['required', 'validSsid']}
                                    errorMessages={[i18n('this_field_is_required'), i18n('wifi_ap_ssid_invalid')]}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextValidator
                                    fullWidth
                                    label={i18n('wifi_ap_passphrase')}
                                    type={showPassphrase ? 'text' : 'password'}
                                    variant="outlined"
                                    name="passphrase"
                                    onChange={handleWifiApChange}
                                    value={wifiApData.passphrase}
                                    validators={['required', 'validPassphrase']}
                                    errorMessages={[i18n('this_field_is_required'), i18n('wifi_ap_passphrase_invalid')]}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle passphrase visibility"
                                                    onClick={() => setShowPassphrase((prev) => !prev)}
                                                    edge="end"
                                                >
                                                    {showPassphrase ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sx={{ textAlign: 'center' }}>
                                <Button disabled={!wifiApValid || helperUnavailable} variant="contained" color="primary" type="submit">
                                    {i18n('wifi_ap_settings')}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </ValidatorForm>

            <Divider sx={{ marginTop: '32px' }} />

            <Grid container spacing={3} sx={{ marginTop: '20px' }}>
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SectionWarningIcon />
                        <Typography variant="h2" color={helperUnavailable ? 'text.secondary' : undefined}>
                            {i18n('power_management')}
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ marginTop: '4px' }}>
                        {i18n('power_management_description')}
                    </Typography>
                </Grid>

                <Grid item xs={12} sx={disabledSection}>
                    <Grid container sx={{ maxWidth: '50%', mx: 'auto', justifyContent: 'center' }}>
                        <Grid item sx={{ display: 'flex', gap: '16px' }}>
                            <Button variant="contained" color="warning" disabled={helperUnavailable} onClick={onReboot}>
                                {i18n('reboot')}
                            </Button>
                            <Button variant="contained" color="error" disabled={helperUnavailable} onClick={onShutdown}>
                                {i18n('shutdown')}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
}

export default SystemTab;
